import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';
import { ArrowRight, Clock, Tag } from 'lucide-react';
import { API } from '../config';


const labels = {
  en: { title: 'Blog', subtitle: 'Guides, comparisons, and tips for getting the most out of your PDFs.', read: 'Read more', empty: 'No articles yet. Stay tuned!' },
  fr: { title: 'Blog', subtitle: 'Guides, comparatifs et astuces pour tirer le meilleur de vos PDF.', read: 'Lire la suite', empty: 'Aucun article pour le moment. Restez connecté !' },
  hi: { title: 'ब्लॉग', subtitle: 'अपने PDF से अधिकतम लाभ लेने के लिए गाइड, तुलना और टिप्स।', read: 'और पढ़ें', empty: 'अभी कोई लेख नहीं। बने रहें!' },
};

export default function BlogPage() {
  const { lang } = useI18n();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const l = labels[lang] || labels.en;

  useEffect(() => {
    axios.get(`${API}/blog`).then(r => setPosts(r.data.posts || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="blog-page">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-blue-400/70 font-medium">Blog</span>
            <h1 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-foreground mt-3" data-testid="blog-title">{l.title}</h1>
            <p className="text-muted-foreground text-lg mt-4 max-w-xl">{l.subtitle}</p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <p className="text-muted-foreground text-center py-20" data-testid="blog-empty">{l.empty}</p>
          ) : (
            <div className="space-y-6">
              {posts.map((post, i) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="group block p-6 rounded-2xl bg-card border border-border hover:border-blue-500/20 transition-all duration-300"
                    data-testid={`blog-card-${post.slug}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h2 className="font-heading text-xl font-semibold text-foreground group-hover:text-blue-400 transition-colors mb-2 truncate">
                          {post.title}
                        </h2>
                        <p className="text-muted-foreground text-sm line-clamp-2 mb-3">{post.excerpt}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(post.created_at).toLocaleDateString()}</span>
                          {post.tags?.length > 0 && (
                            <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{post.tags.slice(0, 3).join(', ')}</span>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-muted-foreground/60 group-hover:text-blue-400 shrink-0 mt-1 transition-colors" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
